import { formatDate } from '../../utils';
import React, { useEffect, useState, useCallback } from 'react';
import {
  Table,
  Button,
  Card,
  Spin,
  Tag,
  Select,
  Row,
  Col,
  Typography,
  Tabs,
  Tooltip,
  Drawer,
  Space,
  DatePicker,
  Input,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { deleteTicket, getAllTickets } from './Service/TicketService';
import NewTicketDrawer from './NewTicketDrawer';
import EditTicketDrawer from './EditTicketDrawer';
import { Ticket } from './Type/Ticket';
import UserService from './../../services/UserService';
import { debounce } from 'lodash';

const { Option } = Select;
const { Title, Text } = Typography;

const TicketList: React.FC = () => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [loading, setLoading] = useState(false);

  // Kullanıcı seçimi için state
  const [users, setUsers] = useState<any[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [userPage, setUserPage] = useState(1);
  const [userTotal, setUserTotal] = useState(0);
  const [userSearchTerm, setUserSearchTerm] = useState('');

  // Durum filtresi (OPEN/CLOSED)
  const [statusFilter, setStatusFilter] = useState<string>('OPEN');
  // Filter Drawer state + filters
  const [filterOpen, setFilterOpen] = useState(false);
  const [appliedUserId, setAppliedUserId] = useState<number | null>(null);
  const [appliedPriority, setAppliedPriority] = useState<'LOW' | 'MEDIUM' | 'HIGH' | null>(null);
  const [appliedDateRange, setAppliedDateRange] = useState<[any, any] | null>(null);
  const [pUserId, setPUserId] = useState<number | null>(null);
  const [pPriority, setPPriority] = useState<'LOW' | 'MEDIUM' | 'HIGH' | null>(null);
  const [pDateRange, setPDateRange] = useState<[any, any] | null>(null);
  // Quick search (subject + description)
  const [quickSearch, setQuickSearch] = useState<string>('');
  // Raw list for client-side filtering
  const [rawTickets, setRawTickets] = useState<Ticket[]>([]);

  // Client-side pagination
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50
  });

  /**
   * Parametrelere göre ticketları getirir.
   */
  const fetchTickets = async (
    accountId?: number | null,
    status: string = 'OPEN',
    override?: { priority?: 'LOW' | 'MEDIUM' | 'HIGH' | null; dateRange?: [any, any] | null; search?: string }
  ) => {
    setLoading(true);
    try {
      // Build filter params per backend spec: field.operation=value
      const filters: string[] = [];
      if (typeof accountId === 'number') filters.push(`accountId.equals=${accountId}`);
      if (status) filters.push(`status.equals=${encodeURIComponent(status)}`);
      // If override contains the key, use it even if null, otherwise fall back to applied state
      const effPriority = (override && Object.prototype.hasOwnProperty.call(override, 'priority'))
        ? override.priority!
        : appliedPriority;
      const effDateRange = (override && Object.prototype.hasOwnProperty.call(override, 'dateRange'))
        ? override.dateRange!
        : appliedDateRange;
      if (effPriority) filters.push(`priority.equals=${effPriority}`);
      if (effDateRange && effDateRange[0] && effDateRange[1]) {
        // Send UTC (Z) timestamps corresponding to local start/end of selected days
        const from = dayjs(effDateRange[0]).startOf('day').toDate().toISOString(); // e.g. 2025-07-17T21:00:00.000Z
        const to = dayjs(effDateRange[1]).endOf('day').toDate().toISOString();     // e.g. 2025-07-20T20:59:59.999Z
        filters.push(`createdAt.greaterThanOrEqual=${encodeURIComponent(from)}`);
        filters.push(`createdAt.lessThanOrEqual=${encodeURIComponent(to)}`);
      }
      const searchTerm = (override?.search || '').trim();
      if (searchTerm.length >= 2) filters.push(`search=${encodeURIComponent(searchTerm)}`);
      const paramStr = filters.length ? `?${filters.join('&')}` : '';
      const data: Ticket[] = await getAllTickets(paramStr);
      const sorted = data.sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setRawTickets(sorted);
      setTickets(sorted);
      setPagination((prev) => ({ ...prev, current: 1 }));
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Kullanıcıları listele
   */
  const fetchUsers = async (page: number = 1, search: string = '') => {
    try {
      const response: any = await UserService.getAll({
        name: search,
        page: page - 1,
        size: 10,
        sortField: 'createdAt',
        sortDirection: 'DESC',
      });
      if (page === 1) {
        setUsers(response.data.content);
      } else {
        setUsers((prev) => [...prev, ...response.data.content]);
      }
      setUserTotal(response.data.totalElements);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  /**
   * İlk açılışta sadece açık ticketlar ve kullanıcıları getir
   */
  useEffect(() => {
    fetchTickets(undefined, 'OPEN');
    fetchUsers();
  }, []);

  /**
   * Kullanıcı seçimi değiştiğinde ticketları yeniden çek
   */
  const handleUserChange = (userId: number | null) => {
    setSelectedUserId(userId);
    fetchTickets(userId, statusFilter);
  };

  /**
   * Tab (status) değişince ticketları çek
   */
  const handleTabChange = (key: string) => {
    // Reset all filters and search when tab (status) changes
    setStatusFilter(key);
    setQuickSearch('');
    setAppliedUserId(null);
    setAppliedPriority(null);
    setAppliedDateRange(null);
    setPUserId(null);
    setPPriority(null);
    setPDateRange(null);
    setSelectedUserId(null);
    setFilterOpen(false);
    fetchTickets(null, key, { priority: null, dateRange: null, search: '' });
  };

  /**
   * Debounce kullanıcı arama
   */
  const debouncedUserSearch = useCallback(
    debounce((value: string) => {
      setUserPage(1);
      setUsers([]);
      fetchUsers(1, value);
    }, 300),
    []
  );

  const handleUserSearch = (value: string) => {
    setUserSearchTerm(value);
    debouncedUserSearch(value);
  };

  const handleUserScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, clientHeight, scrollHeight } = event.target as HTMLElement;
    if (scrollTop + clientHeight >= scrollHeight) {
      if (users.length < userTotal) {
        const nextPage = userPage + 1;
        setUserPage(nextPage);
        fetchUsers(nextPage, userSearchTerm);
      }
    }
  };

  // Keep table data synced with server result (filters applied server-side)
  useEffect(() => {
    setTickets(rawTickets);
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, [rawTickets]);

  // Debounced API search when user stops typing (>=2 chars), and debounced reset on clear
  const debouncedTicketSearch = useCallback(
    debounce((term: string, uid: number | null, status: string, prio: any, range: any) => {
      fetchTickets(uid, status, { priority: prio, dateRange: range, search: term });
    }, 500),
    []
  );
  const debouncedResetSearch = useCallback(
    debounce((uid: number | null, status: string, prio: any, range: any) => {
      fetchTickets(uid, status, { priority: prio, dateRange: range, search: '' });
    }, 300),
    []
  );
  const searchEffectInitialized = React.useRef(false);
  useEffect(() => {
    const term = (quickSearch || '').trim();
    // Skip initial run to avoid duplicate load
    if (!searchEffectInitialized.current) {
      searchEffectInitialized.current = true;
      return;
    }
    if (term.length >= 2) {
      debouncedResetSearch.cancel();
      debouncedTicketSearch(term, selectedUserId, statusFilter, appliedPriority, appliedDateRange);
    } else if (term.length === 0) {
      debouncedTicketSearch.cancel();
      debouncedResetSearch(selectedUserId, statusFilter, appliedPriority, appliedDateRange);
    } else {
      // length 1: do nothing; wait for more typing
      debouncedTicketSearch.cancel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quickSearch]);

  /**
   * Silme işlemi
   */
  const handleDelete = async (id: number) => {
    setLoading(true);
    try {
      await deleteTicket(id);
      await fetchTickets(selectedUserId, statusFilter); // yenile
    } catch (error) {
      console.error('Failed to delete ticket:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Öncelik/durum renklendirme
   */
  const translatePriorityOrStatus = (value: string) => {
    switch (value) {
      case 'LOW':
        return <Tag color="green">Düşük</Tag>;
      case 'MEDIUM':
        return <Tag color="orange">Orta</Tag>;
      case 'HIGH':
        return <Tag color="red">Yüksek</Tag>;
      case 'OPEN':
        return <Tag color="orange">Açık</Tag>;
      case 'CLOSED':
        return <Tag color="green">Kapalı</Tag>;
      default:
        return <Tag>{value}</Tag>;
    }
  };

  const columns = [
    {
      title: 'No',
      dataIndex: 'id',
      key: 'id',
      sorter: (a: Ticket, b: Ticket) => a.id - b.id,
      render: (text: number) => <b>#{text}</b>
    },
    {
      title: 'Konu',
      dataIndex: 'subject',
      key: 'subject',
      width: 250,
      ellipsis: true as any,
      render: (text: string) => (
        <Tooltip title={text}>
          <span style={{ display: 'inline-block', maxWidth: 250, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {text}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'Açıklama',
      dataIndex: 'description',
      key: 'description',
      width: 250,
      ellipsis: true as any,
      render: (text: string) => (
        <Tooltip title={text}>
          <span style={{ display: 'inline-block', maxWidth: 250, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {text}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'Kullanıcı Ad Soyad',
      dataIndex: 'account',
      key: 'account',
      sorter: (a: any, b: any) => (`${a.account.firstName} ${a.account.lastName}`).localeCompare(`${b.account.firstName} ${b.account.lastName}`, 'tr'),
      render: (account: any) => `${account.firstName} ${account.lastName}`,
    },
    {
      title: 'Oluşturma Tarihi',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: (a: Ticket, b: Ticket) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      render: (text: string) => formatDate(text)
    },
    {
      title: 'Öncelik',
      dataIndex: 'priority',
      key: 'priority',
      sorter: (a: Ticket, b: Ticket) => {
        const order: any = { LOW: 0, MEDIUM: 1, HIGH: 2 };
        return (order[a.priority] ?? 0) - (order[b.priority] ?? 0);
      },
      render: (text: string) => translatePriorityOrStatus(text)
    },
    {
      title: '',
      key: 'action',
      render: (text: any, record: Ticket) => (
        <Button
          type="default"
          className="d-button action-button"
          style={{ width: 100 }}
          onClick={() => {
            setSelectedTicket(record);
            setDrawerVisible(true);
          }}
        >Detay</Button>
      ),
    },
  ];

  // Pagination (client-side)
  const handleTableChange = (newPagination: any) => {
    setPagination((prev) => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize || 50
    }));
  };

  const startIndex = (pagination.current - 1) * pagination.pageSize;
  const endIndex = startIndex + pagination.pageSize;
  const slicedData = tickets.slice(startIndex, endIndex);

  return (
    <div className='ticket-list-container'>
      <Tabs
        defaultActiveKey="OPEN"
        activeKey={statusFilter}
        onChange={handleTabChange}
        className="custom-tabs"
      >
        <Tabs.TabPane tab={
          <Button
            type={statusFilter === 'OPEN' ? 'primary' : 'default'}
            className="d-button tab-button"
          >
            Açık Talepler
          </Button>
        } key="OPEN" />
        <Tabs.TabPane tab={
          <Button
            type={statusFilter === 'CLOSED' ? 'primary' : 'default'}
            className="d-button tab-button"
          >
            Kapalı Talepler
          </Button>
        } key="CLOSED" />
      </Tabs>
      <Card className='br-30px'>
        <Row gutter={[16, 16]} align="middle" justify="space-between" className="mb-20px">
          <Col xs={24} sm={12} lg={16}>
            <Row align="bottom">
              <Title level={4} style={{ margin: 0 }}>
                {statusFilter === "OPEN" ? "Açık ": "Kapalı "} Destek Talepleri
              </Title>
              <Text type="secondary" className="ms-5">
                <strong>({tickets.length})</strong> kayıt listelendi
              </Text>
            </Row>
          </Col>

          <Col xs={24} sm={12} lg={8} style={{ textAlign: 'right' }} className="d-flex justify-content-end">
            <Space>
              <Input
                allowClear
                placeholder='Ara'
                className='d-input d-input-filter'
                style={{ width: 240 }}
                value={quickSearch}
                onChange={(e) => setQuickSearch(e.target.value)}
                prefix={<SearchOutlined style={{ color: '#b8b9bc' }} />}
              />
              <Button type="primary" onClick={() => {
                setPUserId(appliedUserId);
                setPPriority(appliedPriority);
                setPDateRange(appliedDateRange);
                setFilterOpen(true);
              }}>Filtrele</Button>
            </Space>
          </Col>
        </Row>

        <Spin spinning={loading}>
          {!loading && <Table
            columns={columns}
            dataSource={slicedData}
            rowKey="id"
            scroll={{ x: 'max-content' }}
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: tickets.length,
              showSizeChanger: true,
              pageSizeOptions: ['50', '100', '200', '500'],
              defaultPageSize: 50,
            }}
            onChange={handleTableChange}
          />}
        </Spin>

        {selectedTicket ? (
          <EditTicketDrawer
            visible={drawerVisible}
            onClose={() => setDrawerVisible(false)}
            ticketId={selectedTicket.id}
            onSave={() => fetchTickets(selectedUserId, statusFilter)}
            initialValues={selectedTicket}
          />
        ) : (
          <NewTicketDrawer
            visible={drawerVisible}
            onClose={() => setDrawerVisible(false)}
            onSave={() => fetchTickets(selectedUserId, statusFilter)}
            initialValues={{ priority: 'LOW' }}
          />
        )}
        <Drawer
          title='Filtre'
          open={filterOpen}
          onClose={() => setFilterOpen(false)}
          destroyOnClose
          width={420}

        >
          <Space direction='vertical' size='large' style={{ width: '100%' }}>
            <div>
              <Typography.Text>Kullanıcı</Typography.Text>
              <Select
                showSearch
                placeholder='Kullanıcı Seçin'
                style={{ width: '100%', marginTop: 6 }}
                value={pUserId as any}
                onChange={(value) => {
                  const v = value as number | null;
                  setPUserId(v);
                }}
                onSearch={handleUserSearch}
                onPopupScroll={handleUserScroll}
                allowClear
                filterOption={false}
              >
                {users.map((user: any) => (
                  <Option key={user.id} value={user.id}>
                    {`${user.firstName} ${user.lastName}`}
                  </Option>
                ))}
              </Select>
            </div>
            <div>
              <Typography.Text>Öncelik</Typography.Text>
              <Select
                allowClear
                placeholder='Seçiniz'
                style={{ width: '100%', marginTop: 6 }}
                value={pPriority as any}
                onChange={(v) => {
                  const p = (v as any) as ('LOW' | 'MEDIUM' | 'HIGH' | null);
                  setPPriority(p);
                }}
                options={[{ value: 'LOW', label: 'Düşük' }, { value: 'MEDIUM', label: 'Orta' }, { value: 'HIGH', label: 'Yüksek' }]}
              />
            </div>
            <div>
              <Typography.Text>Tarih</Typography.Text>
              <DatePicker.RangePicker
                style={{ width: '100%', marginTop: 6 }}
                value={pDateRange as any}
                onChange={(vals) => {
                  const rng = vals as any;
                  setPDateRange(rng);
                }}
                format={'DD/MM/YYYY'}
              />
            </div>

            <div style={{ textAlign: 'right' }}>
              <Space>
                <Button onClick={() => {
                  setPUserId(null);
                  setPPriority(null);
                  setPDateRange(null);
                  setAppliedUserId(null);
                  setAppliedPriority(null);
                  setAppliedDateRange(null);
                  setSelectedUserId(null);
                  fetchTickets(null, statusFilter);
                }}>Temizle</Button>
                <Button type='primary' onClick={() => {
                  // Commit drawer values to applied state
                  setAppliedUserId(pUserId);
                  setAppliedPriority(pPriority);
                  setAppliedDateRange(pDateRange);
                  setSelectedUserId(pUserId);
                  setFilterOpen(false);
                  // Use current pending values explicitly so we don't read stale state
                  fetchTickets(pUserId, statusFilter, { priority: pPriority, dateRange: pDateRange });
                }}>Filtrele</Button>
              </Space>
            </div>
          </Space>
        </Drawer>
      </Card>
    </div>
  );
};

export default TicketList;
